import React from 'react';
import ActivitySection from './ActivitySection';
import SectionState from './SectionState';
import {
  comparisonLabel, dayMonthTime, relativeDay, scoringChange, signedDelta,
} from './lib/activityFormat';
import { ScoreReadout } from './ScoreReadout';
import { ExamGlanceTile } from './ExamStats';
import { scrollToSection } from './lib/scrollToSection';
import { EngagementBody } from './EngagementSection';

// Words read out with each flag, so its colour is never the only signal.
const FLAG_KIND_TEXT = { warn: 'Needs attention', soon: 'Worth a look', todo: 'To do', good: 'Going well' };

// Where each completion count's tile moves to.
const COMPLETION_SECTION = {
  writing: 'writing',
  quiz: 'quizzes',
  practice: 'exams',
  papers: 'exams',
  interviews: 'interviews',
  mastery: 'overview',
  cases: 'cases',
};

const Delta = ({ current, previous, suffix = '' }) => {
  const text = signedDelta(current, previous);
  if (text == null) return null;
  if (text === 'same') return <>{suffix ? 'Same as the period before' : 'same'}</>;
  const tone = current > previous ? 'td-act-up' : 'td-act-down';
  return (
    <>
      <span className={tone}>{text}</span>
      {suffix}
    </>
  );
};

// A flag's detail: its parts each linked to their own section ("1 essay,
// 1 case answer.") when the backend sends links, otherwise the text alone.
const FlagDetail = ({ flag, onJump }) => {
  const links = Array.isArray(flag.links) ? flag.links : [];
  if (!links.length) return <span className="td-act-flag__detail">{flag.detail}</span>;
  return (
    <span className="td-act-flag__detail">
      {links.map((link, i) => (
        <React.Fragment key={link.section}>
          {i ? ', ' : null}
          <button type="button" className="td-linkbtn" onClick={() => onJump(link.section)}>{link.text}</button>
        </React.Fragment>
      ))}
      .
    </span>
  );
};

const Flags = ({ flags, onJump }) => (flags.length ? (
  <ul className="td-act-flags" aria-label="Needs attention">
    {flags.map((f) => (
      <li key={f.code} className={`td-act-flag td-act-flag--${FLAG_KIND_TEXT[f.kind] ? f.kind : 'todo'}`}>
        <span className="td-act-flag__dot" aria-hidden="true" />
        <div>
          <span className="td-act-sr">{`${FLAG_KIND_TEXT[f.kind] || 'Note'}: `}</span>
          <button type="button" className="td-act-flag__title" onClick={() => onJump(f.section)}>{f.title}</button>
          <FlagDetail flag={f} onJump={onJump} />
        </div>
      </li>
    ))}
  </ul>
) : null);

const Glance = ({ stats, generatedAt, examSummary, onJump }) => {
  const days = stats.days_in_period;
  let writingNote = 'Nothing marked in this period';
  if (stats.writing_marked > 0) {
    writingNote = stats.writing_awaiting_comment > 0
      ? `${stats.writing_awaiting_comment} still ${stats.writing_awaiting_comment === 1 ? 'needs' : 'need'} your comment`
      : 'All have your comment';
  }
  return (
    <div className="td-act-glance">
      <div className="td-stat">
        <span className="td-stat__eyebrow">Last active</span>
        <span className="td-stat__value">{relativeDay(stats.last_active_at, generatedAt)}</span>
        <span className="td-stat__caption">{stats.last_active_at ? dayMonthTime(stats.last_active_at) : 'No activity recorded'}</span>
      </div>
      <div className="td-stat">
        <span className="td-stat__eyebrow">Active days</span>
        <span className="td-stat__value">
          {stats.active_days}
          {days != null ? <span className="td-act-of">{` of ${days}`}</span> : null}
        </span>
        <span className="td-stat__caption">
          <Delta current={stats.active_days} previous={stats.active_days_prev} suffix=" on the period before" />
        </span>
      </div>
      <div className="td-stat">
        <span className="td-stat__eyebrow">Writing marked</span>
        <span className="td-stat__value">
          {stats.writing_marked}
          <span className="td-act-of">{stats.writing_marked === 1 ? ' piece' : ' pieces'}</span>
        </span>
        <span className="td-stat__caption">{writingNote}</span>
      </div>
      <ExamGlanceTile examSummary={examSummary} onJump={onJump} />
    </div>
  );
};

// A kind whose count is listed by more than one section (quiz questions:
// custom quizzes in Quiz generator, admissions tests in Exam practice) is a
// card, not a button, with one button per part: a button cannot hold buttons.
const SplitCompletion = ({ c, onJump }) => (
  <div className="td-act-kind td-act-kind--split" role="group" aria-label={c.label}>
    <span className="td-stat__eyebrow">{c.label}</span>
    <span className="td-act-kind__value">{c.count}</span>
    <span className="td-act-kind__delta">
      <Delta current={c.count} previous={c.count_prev} />
    </span>
    <span className="td-act-kind__parts">
      {c.parts.map((part) => (
        <button
          key={part.key}
          type="button"
          className="td-linkbtn td-act-kind__part"
          aria-label={`${part.label}: ${part.count}, show`}
          onClick={() => onJump(part.section)}
        >
          {part.count} {part.label.toLowerCase()}
        </button>
      ))}
    </span>
  </div>
);

const Completions = ({ completions, onJump }) => (
  <div className="td-act-kinds">
    {completions.map((c) => (c.parts && c.parts.length ? (
      <SplitCompletion key={c.key} c={c} onJump={onJump} />
    ) : (
      <button
        key={c.key}
        type="button"
        className="td-act-kind"
        onClick={() => onJump(COMPLETION_SECTION[c.key] || 'overview')}
      >
        <span className="td-stat__eyebrow">{c.label}</span>
        <span className="td-act-kind__value">{c.count}</span>
        <span className="td-act-kind__delta">
          <Delta current={c.count} previous={c.count_prev} />
        </span>
      </button>
    )))}
  </div>
);

/**
 * Overview, from GET …/activity/summary: what needs attention, four
 * at-a-glance tiles and the seven completion counts against the period
 * before. Every title and number is the backend's; only the layout is here.
 * Flags, their parts and the tiles move to their section with `onJump(key)`
 * (lib/scrollToSection by default). Engagement sits under the three tiles
 * and keeps its own loading, empty and error state.
 */
const ActivityOverview = ({
  state, period, onJump = scrollToSection, engagementState = null,
}) => {
  const data = state.status === 'ready' ? state.data : null;
  return (
    <ActivitySection id="overview" title="Overview">
      <SectionState state={state} what="the overview" />
      {data ? (
        <>
          <Flags flags={data.flags} onJump={onJump} />
          <h4 className="td-act-subtitle">At a glance</h4>
          <p className="td-act-section__sub">{comparisonLabel(data.period || period)}</p>
          <Glance stats={data.stats} generatedAt={data.generated_at} examSummary={data.exam_summary} onJump={onJump} />
        </>
      ) : null}
      {engagementState ? <EngagementBody state={engagementState} /> : null}
      {data ? (
        <>
          <h4 className="td-act-subtitle">Scores</h4>
          <p className="td-act-section__sub">All time, out of 100. The period does not change these. These scores are for how closely the student's reasoning matches the model answer.</p>
          <div className="td-act-grid td-act-grid--2">
            {(data.scores || []).map((item) => {
              const casesTile = item.key === 'cases';
              const knowledge = data.scoring?.knowledge?.current;
              return (
                <button
                  key={item.key}
                  type="button"
                  className="td-card td-act-scorebtn"
                  onClick={() => onJump(item.section)}
                >
                  <span className="td-card__title">{item.label}</span>
                  <ScoreReadout
                    score={item.score}
                    changeText={casesTile ? scoringChange(data.scoring?.reasoning?.change) : undefined}
                    caption={casesTile ? (knowledge == null ? 'Knowledge —' : `Knowledge ${knowledge}`) : null}
                  />
                </button>
              );
            })}
          </div>
          <Completions completions={data.completions} onJump={onJump} />
          {data.stats.active_days === 0 ? (
            <p className="td-act-note">No completed work in this period.</p>
          ) : null}
        </>
      ) : null}
    </ActivitySection>
  );
};

export default ActivityOverview;
